import { isReservedRootPath } from "./routePaths";
import type { UnlockPhase } from "./mailingUnlock";
import { OPEN_ARTIST_SLUG, isOpenArtistSlug } from "./artistSlug";

export { OPEN_ARTIST_SLUG };

export type GatedPageKind = "artist" | "bio" | "songs" | "albums" | "videos" | "release";

const YEAR_SEGMENT = /^\d{4}$/;
const FILE_SEGMENT = /\.(?:html?|xml|txt|json|ico|png|jpe?g|gif|svg|webp|avif|m?js|css|map|php|aspx?|env|webmanifest|pdf|mp3|mp4|woff2?|ttf|zip)$/i;
const MONTH_SEGMENT = /^\d{1,2}$/;
const ARTIST_SUB_PAGES = new Set(["bio", "songs", "albums", "videos"]);
const RELEASE_LIST_SEGMENTS = new Set(["songs", "albums"]);

const decodeSegment = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export const splitGatePath = (pathname: string): string[] =>
  String(pathname || "")
    .split("?")[0]
    .split("#")[0]
    .split("/")
    .filter(Boolean)
    .map(decodeSegment);

const REDIRECT_PLATFORMS = new Set(["spotify", "apple", "youtube", "itunes", "soundcloud", "tidal", "amazon"]);

export function redirectPlatformParam(search?: string | null): string | null {
  if (!search) return null;
  try {
    const query = search.startsWith("?") ? search : `?${search}`;
    const value = (new URLSearchParams(query).get("redirect") || "").trim().toLowerCase();
    return REDIRECT_PLATFORMS.has(value) ? value : null;
  } catch {
    return null;
  }
}

export const hasRedirectParam = (search?: string | null): boolean => redirectPlatformParam(search) !== null;

export function gatedArtistSlug(pathname: string): string | null {
  const parts = splitGatePath(pathname);
  if (parts.length === 0 || parts.length > 3) return null;

  const first = parts[0];
  if (!first || FILE_SEGMENT.test(parts[parts.length - 1])) return null;
  if (isReservedRootPath(first)) return null;
  if (YEAR_SEGMENT.test(first) && parts[1] && MONTH_SEGMENT.test(parts[1])) return null;

  const slug = first.toLowerCase();
  if (isOpenArtistSlug(slug)) return null;
  if (parts.length === 3 && !RELEASE_LIST_SEGMENTS.has(parts[1].toLowerCase())) return null;

  return slug;
}

export const isGatedArtistPath = (pathname: string): boolean => gatedArtistSlug(pathname) !== null;

export interface GateLockInput {
  slug: string | null;
  backendReady: boolean;
  resolving: boolean;
  viewerAuthenticated: boolean;
  unlocked: boolean;
}

export function isGateLocked(input: GateLockInput): boolean {
  if (!input.slug || !input.backendReady) return false;
  if (input.resolving) return false;
  if (input.viewerAuthenticated) return false;
  return !input.unlocked;
}

export function gatedPageKind(pathname: string): GatedPageKind {
  const parts = splitGatePath(pathname);
  if (parts.length < 2) return "artist";
  const second = parts[1].toLowerCase();
  if (parts.length === 2 && ARTIST_SUB_PAGES.has(second)) return second as GatedPageKind;
  return "release";
}

export function gateRedirectPlatform(pathname: string, search?: string | null): string | null {
  const platform = redirectPlatformParam(search);
  if (!platform || !gatedArtistSlug(pathname)) return null;
  return gatedPageKind(pathname) === "release" ? platform : null;
}

export function gatedPageLabel(pathname: string, artistName: string): string {
  switch (gatedPageKind(pathname)) {
    case "bio":
      return `the ${artistName} story`;
    case "songs":
      return `every ${artistName} song`;
    case "albums":
      return `the ${artistName} albums`;
    case "videos":
      return `the ${artistName} videos`;
    case "release":
      return "this release";
    default:
      return `the ${artistName} page`;
  }
}

export type GatePrompt = "modal" | "pending" | "collapsed";

export interface GatePromptInput {
  phase: UnlockPhase;
  dismissed: boolean;
}

export function gatePrompt(input: GatePromptInput): GatePrompt {
  if (input.phase === "pending") return "pending";
  return input.dismissed ? "collapsed" : "modal";
}
